import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { DrawerService } from '../../../../core/services/drawer.service';
import { ManageOrganizationService } from '../../manage-organization.service';
import { finalize, Subject, takeUntil, debounceTime } from 'rxjs';
import {
  ManageOrganizationDrawerParams,
  UserApiResponsePaginated,
  UserListModel,
  searchObj,
} from '../../manage-organization.interface';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { OrgManagementMode, UserStatus } from '../../manage-organization.enums';
import { CommonModule } from '@angular/common';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { UserDetailAndEditComponent } from '../user-detail-and-edit/user-detail-and-edit.component';
import { ExcelExportService } from '../../../../shared/services/excel-export.service';
import { format } from 'date-fns';
import { Role, RoleService } from '../../../../shared/services/role.service';
import { AuditContextService } from '../../../audit-logs/audit-context.service';
import _ from 'lodash';
import { CommonService } from '../../../../shared/services/common.service';
import { UserDatePipe } from '../../../../shared/pipes/user-date.pipe';
import { ErrorResponse } from '../../../../core/models/error.models';
@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [
    NzIconModule,
    NzTableModule,
    SvgIconComponent,
    NzDropDownModule,
    NzPaginationModule,
    NzModalModule,
    CommonModule,
    NzButtonModule,
    UserDatePipe,
  ],
  templateUrl: './user-list.component.html',
})
export class UserListComponent implements OnInit, OnDestroy {
  private readonly manageOrgService = inject(ManageOrganizationService);
  private readonly auditContextService = inject(AuditContextService);
  loading = false;
  pageSize = 10;
  pageIndex = 1;
  total = 0;
  sort = '';
  sortOrder: number | undefined = undefined;
  searchTerm = '';
  errorMessage = '';
  listOfColumn = [
    {
      title: '',
    },
    {
      title: 'User Name',
      key: 'email',
      width: 400,
    },
    {
      title: 'Name',
      key: 'name',
    },
    {
      title: 'Last Login',
      key: 'lastLogin',
    },
    {
      title: 'Status',
      key: 'status',
    },
    {
      title: 'Title',
      key: 'title',
      class: 'no-sorting',
    },
    {
      title: '',
    },
  ];
  selectedUserId = 0;
  isOpenDeleteModel = false;
  roleList: Role[] = [];
  userStatus = UserStatus;
  @Input() set isExportUsers(value: boolean) {
    if (value) {
      this.exportData();
    }
  }
  @Output() userExported = new EventEmitter<boolean>();
  private readonly destroy$ = new Subject<void>();
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.VIEW,
  };
  organizationUsers$ = this.manageOrgService.organizationUsers$;

  constructor(
    private readonly drawerService: DrawerService,
    private readonly excelService: ExcelExportService,
    private readonly roleService: RoleService,
    private readonly commonService: CommonService
  ) {
    this.setupDrawerParams();
    this.manageOrgService.organizationUsers$.subscribe(
      (res: UserApiResponsePaginated | null) => {
        if (res) {
          this.total = res?.result.totalRows;
        }
      }
    );
  }

  ngOnInit() {
    this.manageOrgService.search$
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe((searchObj: searchObj) => {
        if (
          this.searchTerm !== searchObj.searchTerm &&
          searchObj.tabIndex === 0
        ) {
          this.searchTerm = searchObj.searchTerm;
          this.pageIndex = 1;
          this.loadData();
        }
      });
    this.getRoles();
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  loadData(): void {
    this.loading = true;
    this.manageOrgService
      .getOrgUsers({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        sortOrder: this.sortOrder,
        sort: this.sort,
        searchTerm: this.searchTerm,
        organizationId: Number(this.orgParams.organizationId),
      })
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  getRoles() {
    this.roleService.getAllRoles().subscribe(res => {
      this.roleList = res.result;
    });
  }

  exportData() {
    this.manageOrgService
      .getAllUsers({
        pageSize: this.total,
        organizationId: Number(this.orgParams.organizationId),
      })
      .subscribe(async res => {
        if (!!res && res.result.totalRows > 0) {
          const data = res.result.items.map(user => {
            const roles = user.roles
              ? user.roles.split(',').map(item => item.trim())
              : [];
            return {
              ...user,

              lastLoginFormated: user.lastLogin
                ? format(user.lastLogin, 'dd-MM-yyyy hh:mm a')
                : 'N/A',
              ..._.reduce(
                this.roleList,
                (acc: Record<string, string>, item) => {
                  const key = _.camelCase(item.name);
                  acc[key] = _.includes(roles, item.name) ? 'True' : 'False';
                  return acc;
                },
                {}
              ),
            };
          });
          const headers = {
            name: 'Name',
            email: 'User Name',
            status: 'Status',
            title: 'Title',
            organizationName: 'Organization',
            lastLoginFormated: 'Last Login Date',
            ..._.reduce(
              this.roleList,
              (acc: Record<string, string>, item) => {
                const key = _.camelCase(item.name);
                acc[key] = item.name;
                return acc;
              },
              {}
            ),
          };

          try {
            await this.excelService.exportToExcel(data, 'User_Data', headers);
            this.logExportAudit();
          } catch {
            this.logExportFailureAudit();
          }

          this.userExported.emit(true);
        } else {
          this.commonService.notify('info', 'No user data available to export');
          this.userExported.emit(true);
        }
      });
  }

  onQueryParamsChange(params: NzTableQueryParams) {
    this.pageSize = params.pageSize;
    this.pageIndex = params.pageIndex;
    const currentSort = params.sort.find(item => item.value !== null);
    this.sort = currentSort?.key || '';
    let sortOrder: number | undefined = undefined;
    if (!!currentSort && currentSort.value) {
      sortOrder = currentSort.value === 'ascend' ? 0 : 1;
    }
    this.sortOrder = sortOrder;
    this.loadData();
  }

  viewOrganizationUser(data: UserListModel) {
    if (data) {
      this.selectedUserId = Number(data.id);
      this.drawerService.openDrawer(
        UserDetailAndEditComponent,
        'User Details',
        1170,
        true,
        undefined,
        {
          userId: Number(data.id),
          organizationId: this.orgParams.organizationId,
          organizationName: data.organizationName,
          mode: OrgManagementMode.VIEW,
        },
        DRAWER_IDS.USER_DETAIL
      );
    }
  }

  showDeleteModal(userId: number | undefined) {
    if (userId) {
      this.selectedUserId = userId;
      this.isOpenDeleteModel = true;
    }
  }
  handleOkMiddle(): void {
    this.isOpenDeleteModel = false;
    this.deleteUser(this.selectedUserId);
  }
  handleCancelMiddle(): void {
    this.isOpenDeleteModel = false;
    this.selectedUserId = 0;
  }

  deleteUser(userId: number) {
    this.manageOrgService
      .deleteOrgUser(userId, Number(this.orgParams.organizationId))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            'User has been deleted successfully'
          );
          this.loadData();
          this.selectedUserId = 0;
        },
        error: error => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  resendEmail(userId: number) {
    const userIds = [userId];
    const orgId = this.orgParams.organizationId;

    // Find user info for audit enrichment
    let user: UserListModel | undefined;
    const currentUsers = this.getCurrentUserFromObservable();
    if (currentUsers) {
      user = currentUsers.find((u: UserListModel) => u.id === userId);
    }
    // Build enriched audit log for resend email
    const enriched = {
      user: user ? `${user.name} - ${user.email}` : `UserId: ${userId}`,
      organizationId: orgId,
    };
    const modifiedFields = this.auditContextService.buildFieldChanges(
      {},
      enriched,
      {
        sensitiveFields: ['email'],
        labelMap: { user: 'User', organizationId: 'Organization' },
        excludeFields: ['organizationId'],
      }
    );

    this.manageOrgService
      .resendEmailToUser(userIds, orgId, modifiedFields)
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            'Email Invitation has been sent successfully'
          );
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  private getCurrentUserFromObservable(): UserListModel[] {
    let users: UserListModel[] = [];
    this.organizationUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: UserApiResponsePaginated | null) => {
        if (res?.result?.items) {
          users = res.result.items;
        }
      });
    return users;
  }

  private logExportAudit(): void {
    this.manageOrgService
      .logUserExportAudit(Number(this.orgParams.organizationId))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private logExportFailureAudit(): void {
    this.manageOrgService
      .logUserExportFailureAudit(Number(this.orgParams.organizationId))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
